
import React, { useEffect, useState } from 'react';
import { Tooltip, IconButton } from '@mui/material';

export default function DrugSearchResultCard({
  drug,
  onClick,
  onAddToCart,
  isInCart,
  onVideoCall,
  onAudioCall,
  onMessage
}) {
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  const [distanceInfo, setDistanceInfo] = useState({ distance: '', duration: '' });

  // Calculate distance and duration
  useEffect(() => {
    const calculateDistance = async () => {
      if (!drug.clinicLocation || drug.clinicLocation === 'online' || !window.google) {
        return;
      }

      try {
        // Get user's current location
        const getUserLocation = () =>
          new Promise((resolve) => {
            if (!navigator.geolocation) {
              resolve({ lat: 6.5244, lng: 3.3792 }); // Default to Lagos
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              () => resolve({ lat: 6.5244, lng: 3.3792 }),
              { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 }
            );
          });

        const origin = await getUserLocation();

        // Geocode destination
        const geocoder = new window.google.maps.Geocoder();
        const geocodeResult = await geocoder.geocode({ address: drug.clinicLocation });
        
        if (!geocodeResult.results[0]) return;

        // Calculate distance
        const directionsService = new window.google.maps.DirectionsService();
        const result = await directionsService.route({
          origin,
          destination: { placeId: geocodeResult.results[0].place_id },
          travelMode: window.google.maps.TravelMode.DRIVING,
        });

        if (result.status === 'OK') {
          const leg = result.routes[0]?.legs[0];
          if (leg) {
            setDistanceInfo({
              distance: leg.distance.text,
              duration: leg.duration.text,
            });
          }
        }
      } catch (error) {
        console.error('Error calculating distance:', error);
      }
    };

    calculateDistance();
  }, [drug.clinicLocation]);

  // Product picture (for the drug itself)
  const productImage = drug.productPicture
    ? `${mediaUrl}/${drug.productPicture}`
    : null;

  // Pharmacy/clinic logo
  const pharmacyLogo = drug.logoPath
    ? `${mediaUrl}/${drug.logoPath}`
    : null;

  // Get category badge styling
  const getCategoryBadge = () => {
    const categoryType = drug.categoryType;
    const badges = {
      medication: { label: 'Medication', color: 'bg-blue-100 text-blue-700', icon: '💊' },
      lab: { label: 'Lab Test', color: 'bg-purple-100 text-purple-700', icon: '🔬' },
      equipment: { label: 'Equipment', color: 'bg-orange-100 text-orange-700', icon: '🏥' },
      bed: { label: 'Hospital Bed', color: 'bg-pink-100 text-pink-700', icon: '🛏️' },
      service: { label: 'Service', color: 'bg-teal-100 text-teal-700', icon: '⚕️' },
    };
    return badges[categoryType] || badges.medication;
  };

  const categoryBadge = getCategoryBadge();

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer relative"
      style={{ width: '264px' }}
    >


      {/* Product Image (if available) */}
      {productImage && (
        <div className="w-full h-32 sm:h-40 bg-gray-50 flex items-center justify-center p-4">
          <img
            src={productImage}
            alt={drug.productName}
            className="max-h-full max-w-full object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Pharmacy Logo Badge (top right) */}
      {pharmacyLogo && (
        <div className="absolute top-2 right-2 w-12 h-12 bg-white rounded-full shadow-md p-1.5 border-2 border-gray-100">
          <img
            src={pharmacyLogo}
            alt={drug.clinicName}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Price + badge */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <p className="text-lg font-bold text-gray-900">
            ₦{(drug.sellingPrice || drug.cost || drug?.unitCost)?.toLocaleString() ?? '0'}
          </p>
          {isInCart && (
            <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0">
              Selected
            </span>
          )}
        </div>

        {/* Product/Service Name - Made prominent */}
        <h3 className="font-bold text-gray-900 text-base mb-2 line-clamp-2 break-words">
          {drug.productName || drug.name || drug.itemName || 'N/A'}
        </h3>

        {/* Pharmacy/Clinic Name */}
        <p className="text-xs text-gray-600 mb-2 line-clamp-2 break-words">
          {drug.clinicName || 'N/A'}
        </p>

        {/* Location */}
        <div className="flex items-start text-xs text-gray-600 mb-2">
          <svg className="text-gray-400 mr-1 flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span className="break-words line-clamp-2">
            {drug.clinicLocation && drug.clinicLocation !== 'online'
              ? drug.clinicLocation
              : 'N/A'}
          </span>
        </div>

        {/* Distance and Duration */}
        {distanceInfo.distance && (
          <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
            <div className="flex items-center">
              <svg className="text-gray-400 mr-1" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>{distanceInfo.distance}</span>
            </div>
            <div className="flex items-center">
              <svg className="text-gray-400 mr-1" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14" stroke="white" strokeWidth="2" fill="none"/>
              </svg>
              <span>{distanceInfo.duration}</span>
            </div>
          </div>
        )}

        {/* Category Badge */}
        <div className="flex items-start text-xs text-gray-600 mb-2">
          <span className={`${categoryBadge.color} px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1`}>
            <span>{categoryBadge.icon}</span>
            <span>{categoryBadge.label}</span>
          </span>
        </div>

        {/* Rating */}
        {/* <div className="flex items-center">
          <div className="flex mr-1.5">{renderStars()}</div>
          <span className="text-xs sm:text-sm text-gray-600">4.8 (10 reviews)</span>
        </div> */}
      </div>

      {/* Action Buttons - All Disabled */}
      <div className="px-4 pb-4">
        <div className="flex gap-2 mb-3">
          <Tooltip title="Add to Cart (Disabled)" arrow placement="top">
            <button
              disabled
              className="flex-1 bg-gray-300 text-gray-500 rounded-lg py-2.5 flex items-center justify-center cursor-not-allowed gap-1.5 font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
              <span className="text-xs">Add</span>
            </button>
          </Tooltip>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Tooltip title="Message Pharmacy (Disabled)" arrow placement="top">
            <span>
              <button
                disabled
                className="w-full border-2 border-gray-300 rounded-lg p-2 text-gray-400 cursor-not-allowed flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
                </svg>
              </button>
            </span>
          </Tooltip>

          <Tooltip title="Video Call (Disabled)" arrow placement="top">
            <span>
              <button
                disabled
                className="w-full border-2 border-gray-300 rounded-lg p-2 text-gray-400 cursor-not-allowed flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              </button>
            </span>
          </Tooltip>

          <Tooltip title="Audio Call (Disabled)" arrow placement="top">
            <span>
              <button
                disabled
                className="w-full border-2 border-gray-300 rounded-lg p-2 text-gray-400 cursor-not-allowed flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="w-full text-green-600 font-medium hover:underline text-xs text-center"
        >
          View Details
        </button>
      </div>
    </div>
  );
}
